import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { FirebaseAuthService } from '../services/auth/firebase-auth.service';
import { MembershipService } from '../services/auth/membership.service';
import { UserProfileService } from '../services/auth/user-profile.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { BusinessCsfService } from '../services/database/business-csf.service';
import { FirestoreService } from '../services/database/firestore.service';
import {
  EditPatientDialogComponent,
  EDIT_PATIENT_DIALOG_OPTIONS,
} from '../components/edit-patient-dialog/edit-patient-dialog.component';
import { Patient } from '../models/patient.type';
import { VisibleAppointment } from '../models/appointment.type';
import { GuideAnchors } from './guide-anchors';
import { placeGuideCard } from './guide-place';
import { GUIDE_TOURS, guideTour, isTourReady, visibleSteps } from './guide-tours';
import {
  GuideAppointmentDemo,
  GuideConsultorioTab,
  GuideContext,
  GuideFacturacionTab,
  GuideStep,
  GuideTour,
} from './guide.types';

const CARD_WIDTH = 320;
const CARD_HEIGHT = 200;
/** How long to wait for a menu animation or a late anchor before skipping the step. */
const ANCHOR_WAIT_MS = 200;
const ANCHOR_WAIT_LIMIT = 8;

function centeredCard(viewport: { width: number; height: number }): { top: number; left: number } {
  return {
    top: Math.max(12, (viewport.height - CARD_HEIGHT) / 2),
    left: Math.max(12, (viewport.width - CARD_WIDTH) / 2),
  };
}

function isOnScreen(rect: DOMRect, viewport: { width: number; height: number }): boolean {
  if (rect.width < 1 || rect.height < 1) {
    return false;
  }
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  return centerX >= 0 && centerY >= 0 && centerX <= viewport.width && centerY <= viewport.height;
}

function isVerticallyOffscreen(rect: DOMRect, viewport: { width: number; height: number }): boolean {
  const horizontallyIn = rect.right > 0 && rect.left < viewport.width;
  const verticallyOut = rect.bottom < 0 || rect.top > viewport.height;
  return horizontallyIn && verticallyOut;
}

function isAuthUrl(url: string): boolean {
  return (
    url.startsWith('/login') ||
    url.startsWith('/signup') ||
    url.startsWith('/onboarding') ||
    url.startsWith('/verificar-correo') ||
    url.startsWith('/no-business') ||
    url.startsWith('/invitacion')
  );
}

@Injectable({ providedIn: 'root' })
export class GuideService {
  private readonly auth = inject(FirebaseAuthService);
  private readonly profile = inject(UserProfileService);
  private readonly membership = inject(MembershipService);
  private readonly flags = inject(FeatureFlagService);
  private readonly csfs = inject(BusinessCsfService);
  private readonly anchors = inject(GuideAnchors);
  private readonly router = inject(Router);
  private readonly firestore = inject(FirestoreService);
  private readonly dialog = inject(MatDialog);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  private readonly tour = signal<GuideTour | null>(null);
  /** Tour ids finished in this session, before the profile write comes back. */
  private readonly finished = signal<string[]>([]);
  readonly index = signal(0);
  readonly target = signal<{ top: number; left: number; width: number; height: number } | null>(null);
  readonly card = signal<{ top: number; left: number } | null>(null);
  /** Set while a step wants the consultas screen opened on a real patient. */
  readonly appointmentDemo = signal<GuideAppointmentDemo | null>(null);
  /** Set while a facturación step needs Catálogo SAT or Receptores SAT open. */
  readonly facturacionTab = signal<GuideFacturacionTab | null>(null);
  /** Set while a step needs Información de pacientes or Información de consultas open. */
  readonly consultorioTab = signal<GuideConsultorioTab | null>(null);
  /** True once the active clinic has an authorized invoicing profile. */
  private readonly invoicingReady = signal(false);

  readonly step = computed<GuideStep | null>(() => {
    const tour = this.tour();
    return tour ? tour.steps[this.index()] ?? null : null;
  });

  readonly stepCount = computed(() => this.tour()?.steps.length ?? 0);
  readonly active = computed(() => this.step() != null);

  private anchorWait: ReturnType<typeof setTimeout> | null = null;
  private positionWatch = 0;
  private waitingForStep: string | null = null;
  private anchorWaits = 0;
  private preparedStep: string | null = null;
  private exampleTarget: Promise<{ patient: Patient; appointment: VisibleAppointment } | null> | null = null;
  private patientDialog: MatDialogRef<EditPatientDialogComponent> | null = null;

  constructor() {
    effect((onCleanup) => {
      const businessId = this.membership.selectedBusinessId();
      if (!this.auth.isLoggedIn() || !businessId || !this.flags.isEnabled('invoicing')) {
        this.invoicingReady.set(false);
        return;
      }
      const stop = this.csfs.watch(
        businessId,
        (rows) => this.invoicingReady.set(rows.some((row) => row.status === 'authorized')),
        () => this.invoicingReady.set(false)
      );
      onCleanup(stop);
    });

    effect(() => {
      if (!this.auth.isLoggedIn()) {
        this.tour.set(null);
        this.finished.set([]);
        return;
      }
      if (!this.flags.isEnabled('guides')) {
        if (this.tour()) {
          this.pause();
        }
        return;
      }
      if (!this.profile.profileReady() || !this.membership.selectedBusinessId()) {
        return;
      }
      if (isAuthUrl(this.url()) || this.tour()) {
        return;
      }
      const ctx = this.context();
      const next = GUIDE_TOURS.find((tour) => !ctx.completed.has(tour.id) && isTourReady(tour, ctx));
      if (next) {
        this.begin(next);
      }
    });

    effect(() => {
      const step = this.step();
      this.anchors.version();
      this.url();
      this.facturacionTab.set(step?.facturacionTab ?? null);
      this.consultorioTab.set(step?.consultorioTab ?? null);
      if (!step) {
        this.clearAnchorWait();
        this.target.set(null);
        this.card.set(null);
        return;
      }
      this.place(step);
    });
  }

  /** Starts a tour again, even if this person already finished it. */
  replay(tourId: string): void {
    if (!this.flags.isEnabled('guides')) {
      return;
    }
    const tour = guideTour(tourId);
    if (tour) {
      this.begin(tour);
    }
  }

  next(): void {
    const tour = this.tour();
    if (!tour) {
      return;
    }
    if (this.index() >= tour.steps.length - 1) {
      void this.finish(tour.id);
      return;
    }
    this.index.update((value) => value + 1);
  }

  back(): void {
    if (this.index() > 0) {
      this.index.update((value) => value - 1);
    }
  }

  skip(): void {
    const tour = this.tour();
    if (tour) {
      void this.finish(tour.id);
    }
  }

  /** Re-measures after scroll or resize. */
  refresh(): void {
    const step = this.step();
    if (step) {
      this.place(step);
    }
  }

  private context(): GuideContext {
    return {
      patients: this.membership.selectedBusinessBilling()?.usage.patients ?? 0,
      completed: new Set([
        ...(this.profile.profile()?.guidesCompleted ?? []),
        ...this.finished(),
      ]),
      invoicing: this.flags.isEnabled('invoicing'),
      invoicingReady: this.invoicingReady(),
      owner: this.membership.isSelectedBusinessOwner(),
      clinical: this.membership.canManageClinicalRecords(),
      doctor: this.membership.canAccessAppointments(),
      secretary: this.membership.isSelectedBusinessSecretary(),
      contador: this.membership.isSelectedBusinessContador(),
    };
  }

  /** Drops the open tour without marking it finished, so it can start again when the flag is on. */
  private pause(): void {
    this.clearAnchorWait();
    this.patientDialog?.close();
    this.patientDialog = null;
    this.appointmentDemo.set(null);
    this.facturacionTab.set(null);
    this.consultorioTab.set(null);
    this.tour.set(null);
  }

  private begin(tour: GuideTour): void {
    if (!this.flags.isEnabled('guides')) {
      return;
    }
    const steps = visibleSteps(tour, this.context());
    if (steps.length === 0) {
      return;
    }
    const visible = { ...tour, steps };
    this.preparedStep = null;
    this.exampleTarget = null;
    this.index.set(0);
    this.tour.set(visible);
    const route = visible.steps[0]?.route;
    if (route && !this.router.url.startsWith(route)) {
      void this.router.navigateByUrl(route);
    }
  }

  private place(step: GuideStep): void {
    this.clearAnchorWait();
    if (this.waitingForStep !== step.id) {
      this.waitingForStep = step.id;
      this.anchorWaits = 0;
    }
    if (step.action && this.preparedStep !== step.id) {
      this.preparedStep = step.id;
      void this.prepare(step);
      return;
    }
    if (step.route && !step.action && !this.router.url.startsWith(step.route)) {
      this.target.set(null);
      void this.router.navigateByUrl(step.route);
      return;
    }
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const element = this.anchors.get(step.anchor);
    const rect = element?.getBoundingClientRect();
    if (!element || !rect || !isOnScreen(rect, viewport)) {
      if (element && rect && this.anchorWaits === 0 && isVerticallyOffscreen(rect, viewport)) {
        element.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
      this.target.set(null);
      this.card.set(centeredCard(viewport));
      this.anchorWaits += 1;
      if (this.anchorWaits > ANCHOR_WAIT_LIMIT) {
        if (!step.action) {
          this.advancePastMissing();
        }
        return;
      }
      this.anchorWait = setTimeout(() => {
        this.anchorWait = null;
        if (this.step()?.id === step.id) {
          this.place(step);
        }
      }, ANCHOR_WAIT_MS);
      return;
    }
    this.anchorWaits = 0;
    const pad = 6;
    const box = {
      top: Math.max(8, rect.top - pad),
      left: Math.max(8, rect.left - pad),
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    };
    this.target.set(box);
    this.card.set(placeGuideCard(box, CARD_WIDTH, CARD_HEIGHT, viewport));
    this.follow(step, element);
  }

  /** Dialogs slide into place after the first measurement. Keep the spotlight on the target. */
  private follow(step: GuideStep, element: HTMLElement): void {
    const watchId = ++this.positionWatch;
    let frames = 24;
    const tick = () => {
      if (watchId !== this.positionWatch || this.step()?.id !== step.id || frames <= 0) {
        return;
      }
      frames -= 1;
      const rect = element.getBoundingClientRect();
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      if (!isOnScreen(rect, viewport)) {
        requestAnimationFrame(tick);
        return;
      }
      const current = this.target();
      const top = Math.max(8, rect.top - 6);
      const left = Math.max(8, rect.left - 6);
      const drifted =
        !current ||
        Math.abs(current.top - top) > 1 ||
        Math.abs(current.left - left) > 1 ||
        Math.abs(current.width - (rect.width + 12)) > 1;
      if (drifted) {
        this.place(step);
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private advancePastMissing(): void {
    const tour = this.tour();
    if (!tour) {
      return;
    }
    if (this.index() >= tour.steps.length - 1) {
      void this.finish(tour.id);
      return;
    }
    this.index.update((value) => value + 1);
  }

  private clearAnchorWait(): void {
    if (this.anchorWait) {
      clearTimeout(this.anchorWait);
      this.anchorWait = null;
    }
  }

  private async prepare(step: GuideStep): Promise<void> {
    const target = await this.example();
    if (this.step()?.id !== step.id) {
      return;
    }
    if (!target?.patient.id || !target.appointment.id) {
      this.place(step);
      return;
    }
    if (step.action === 'open-patient-file') {
      this.appointmentDemo.set(null);
      this.openPatientFile(target.patient, step);
    } else if (step.action === 'open-patient-appointment') {
      this.patientDialog?.close();
      this.patientDialog = null;
      this.appointmentDemo.set({
        patientId: target.patient.id,
        appointmentId: target.appointment.id,
      });
      if (step.route && !this.router.url.startsWith(step.route)) {
        await this.router.navigateByUrl(step.route);
      }
    }
    if (this.step()?.id === step.id) {
      this.place(step);
    }
  }

  private example(): Promise<{ patient: Patient; appointment: VisibleAppointment } | null> {
    if (!this.exampleTarget) {
      this.exampleTarget = this.firestore.findPatientWithAppointment().catch((err) => {
        console.error('[Guide] no se encontró un paciente con consulta', err);
        return null;
      });
    }
    return this.exampleTarget;
  }

  private openPatientFile(patient: Patient, step: GuideStep): void {
    if (this.patientDialog) {
      return;
    }
    this.patientDialog = this.dialog.open(EditPatientDialogComponent, {
      ...EDIT_PATIENT_DIALOG_OPTIONS,
      data: {
        patient,
        updateResults: () => {},
        initialTab: step.patientTab,
      },
    });
    this.patientDialog.afterClosed().subscribe(() => {
      this.patientDialog = null;
    });
  }

  private async finish(tourId: string): Promise<void> {
    this.finished.update((ids) => (ids.includes(tourId) ? ids : [...ids, tourId]));
    this.patientDialog?.close();
    this.patientDialog = null;
    this.appointmentDemo.set(null);
    this.facturacionTab.set(null);
    this.consultorioTab.set(null);
    this.tour.set(null);
    try {
      await this.profile.completeGuide(tourId);
    } catch (err) {
      console.error('[Guide] no se pudo guardar el avance', err);
    }
  }
}
